import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { taxCodesFor, useAccounts, useTaxCodes } from '@/lib/accounts';
import { ApiError, api } from '@/lib/api';
import { formatTrn } from '@/lib/clients';
import { COUNTRIES, LIST_PATH, countryName, type Address, type Contact } from '@/lib/contacts';
import { useLanguage, useT } from '@/lib/i18n';
import { useCurrencies, useHomeCurrency } from '@/lib/currencies';
import { useOrgId } from '@/lib/org';
import { EMIRATES, TERMS, emirateLabel, termsLabel, useCompanySettings } from '@/lib/settings';
import { contactProblems } from '@/lib/contact-form';
import { GST_STATES } from '@/lib/gst';
import { homeCurrencyLabel, isIndia, orgLocale, useArabicEnabled } from '@/lib/org-locale';
import { ArabicField } from '@/components/arabic-field';
import { arabicValue, nameFor } from '@/lib/arabic';

interface AddressForm {
  line1: string;
  line2: string;
  city: string;
  emirate: string;
  poBox: string;
  /** India: PIN code and GST state. */
  postalCode: string;
  state: string;
  country: string;
}

interface Form {
  kind: 'customer' | 'vendor';
  displayName: string;
  displayNameAr: string;
  companyName: string;
  companyNameAr: string;
  /** '' = the company default. */
  documentLanguage: '' | 'en' | 'ar' | 'both';
  contactName: string;
  email: string;
  phone: string;
  mobile: string;
  website: string;
  billing: AddressForm;
  sameAsBilling: boolean;
  shipping: AddressForm;
  vatRegistered: boolean;
  trn: string;
  gstin: string;
  defaultTaxCodeId: string;
  paymentTermsDays: string;
  defaultAccountId: string;
  bankName: string;
  iban: string;
  notes: string;
  /** '' = home currency. */
  currency: string;
}

// a new contact is in the client's country: India clients default to India and their own state
const blankAddress = (): AddressForm => ({
  line1: '',
  line2: '',
  city: '',
  emirate: '',
  poBox: '',
  postalCode: '',
  state: isIndia() ? (orgLocale().stateCode ?? '') : '',
  country: isIndia() ? 'IN' : 'AE',
});
const fromAddress = (a: Address | null, state = ''): AddressForm =>
  a
    ? {
        line1: a.line1 ?? '',
        line2: a.line2 ?? '',
        city: a.city ?? '',
        emirate: a.emirate ?? '',
        poBox: a.poBox ?? '',
        postalCode: a.postalCode ?? '',
        state,
        country: a.country,
      }
    : { ...blankAddress(), state };
const sameAddress = (a: AddressForm, b: AddressForm) =>
  (Object.keys(a) as Array<keyof AddressForm>).every((k) => a[k] === b[k]);
const emptyAddress = (a: AddressForm) =>
  !a.line1 && !a.line2 && !a.city && !a.poBox && !a.emirate && !a.postalCode;

function initial(kind: 'customer' | 'vendor', terms: number, c?: Contact | null, name = ''): Form {
  if (!c)
    return {
      kind,
      displayName: name,
      displayNameAr: '',
      companyName: '',
      companyNameAr: '',
      documentLanguage: '',
      contactName: '',
      email: '',
      phone: '',
      mobile: '',
      website: '',
      billing: blankAddress(),
      sameAsBilling: true,
      shipping: blankAddress(),
      vatRegistered: false,
      trn: '',
      gstin: '',
      defaultTaxCodeId: '',
      paymentTermsDays: String(terms),
      defaultAccountId: '',
      bankName: '',
      iban: '',
      notes: '',
      currency: '',
    };
  const billing = fromAddress(c.billingAddress, c.stateCode ?? '');
  const shipping = c.shippingAddress ? fromAddress(c.shippingAddress) : billing;
  return {
    kind,
    displayName: c.displayName,
    displayNameAr: c.displayNameAr ?? '',
    companyName: c.companyName ?? '',
    companyNameAr: c.companyNameAr ?? '',
    documentLanguage: c.documentLanguage ?? '',
    contactName: c.contactName ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    mobile: c.mobile ?? '',
    website: c.website ?? '',
    billing,
    sameAsBilling: !c.shippingAddress || sameAddress(billing, shipping),
    shipping,
    vatRegistered: c.vatRegistered,
    trn: c.trn ? formatTrn(c.trn) : '',
    gstin: c.gstin ?? '',
    defaultTaxCodeId: c.defaultTaxCodeId ?? '',
    paymentTermsDays: String(c.paymentTermsDays),
    defaultAccountId: c.defaultAccountId ?? '',
    bankName: c.bankName ?? '',
    iban: c.iban ?? '',
    notes: c.notes ?? '',
    currency: c.currency,
  };
}

/**
 * Customer / vendor form drawer (document 06 S11, E9; QBO's customer drawer): name and contact,
 * addresses, VAT, payments, notes. Opening balances come from the opening balance tool (epic 9).
 */
export function ContactDrawer({
  open,
  onOpenChange,
  kind,
  contact,
  initialName,
  chooseKind,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: 'customer' | 'vendor';
  contact?: Contact | null;
  initialName?: string;
  chooseKind?: boolean;
  onSaved?: (contact: Contact) => void;
}) {
  const t = useT();
  const lang = useLanguage();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const settings = useCompanySettings();
  const currencies = useCurrencies();
  const homeFallback = useHomeCurrency();
  const home = homeFallback;
  const taxCodes = useTaxCodes();
  const accounts = useAccounts();
  const terms =
    kind === 'vendor'
      ? (settings.data?.expenses.defaultBillTermsDays ?? 30)
      : (settings.data?.sales.defaultTermsDays ?? 30);
  const [form, setForm] = useState<Form>(() => initial(kind, terms));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const arabicOn = useArabicEnabled();
  const setAddr = (which: 'billing' | 'shipping', k: keyof AddressForm, v: string) =>
    setForm((f) => ({ ...f, [which]: { ...f[which], [k]: v } }));

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(initial(kind, terms, contact, initialName));
    // terms only matter for a new contact; re-reading them must not reset a form being typed in
  }, [open, contact, kind, initialName]);

  const side = form.kind === 'customer' ? 'sales' : 'purchases';
  const codes = taxCodesFor(taxCodes.data, side);
  const expenseAccounts = useMemo(
    () =>
      (accounts.data ?? []).filter(
        (a) => !a.isControl && (a.class === 'expense' || a.class === 'asset'),
      ),
    [accounts.data],
  );

  const save = useMutation({
    mutationFn: async () => {
      const trn = form.trn.replace(/[\s-]+/g, '');
      const abroad = form.billing.country !== 'AE';
      const inIndia = form.billing.country === 'IN';
      const gstin = form.gstin.replace(/\s+/g, '').toUpperCase();
      const address = (a: AddressForm) => ({
        line1: a.line1,
        line2: a.line2,
        city: a.city,
        emirate: a.country === 'AE' ? a.emirate : '',
        poBox: a.country === 'IN' ? '' : a.poBox,
        postalCode: a.country === 'IN' ? a.postalCode : '',
        country: a.country,
      });
      const shipping = form.sameAsBilling ? form.billing : form.shipping;
      const body = {
        ...(contact && !chooseKind ? {} : { kind: form.kind }),
        displayName: form.displayName.trim(),
        companyName: form.companyName,
        // Arabic is sent only while it is on, so switching it off never clears a name
        ...(arabicOn
          ? {
              displayNameAr: arabicValue(form.displayNameAr),
              companyNameAr: arabicValue(form.companyNameAr),
              documentLanguage: form.documentLanguage || null,
            }
          : {}),
        contactName: form.contactName,
        email: form.email,
        phone: form.phone,
        mobile: form.mobile,
        website: form.website,
        country: form.billing.country,
        emirate: abroad ? null : form.billing.emirate,
        billingAddress: address(form.billing),
        shippingAddress: emptyAddress(shipping) ? null : address(shipping),
        ...(inIndia
          ? {
              gstin: gstin || null,
              stateCode: (gstin ? gstin.slice(0, 2) : form.billing.state) || null,
            }
          : {
              vatRegistered: abroad ? false : form.vatRegistered,
              trn: abroad ? null : trn || null,
              ...(isIndia() ? { gstin: null, stateCode: null } : {}),
            }),
        defaultTaxCodeId: form.defaultTaxCodeId || null,
        paymentTermsDays: Number(form.paymentTermsDays),
        ...(form.kind === 'vendor'
          ? {
              defaultAccountId: form.defaultAccountId || null,
              bankName: form.bankName,
              iban: form.iban,
            }
          : {}),
        notes: form.notes,
        // multi-currency: fixed once the contact has transactions (the API refuses a change)
        ...(form.currency && (!contact || !contact.hasTransactions)
          ? { currency: form.currency }
          : {}),
      };
      return contact
        ? api<Contact>(`/orgs/${orgId}/contacts/${contact.id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          })
        : api<Contact>(`/orgs/${orgId}/contacts`, { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ['contacts', orgId] });
      toast(t('salesPages.recordDrawer.saved', { name: saved.displayName }), {
        link: {
          to: `${LIST_PATH[form.kind]}/${saved.id}`,
          label: t(`salesPages.contactDrawer.view.${form.kind}`),
        },
      });
      onSaved?.(saved);
      onOpenChange(false);
    },
    onError: (e) => {
      if (e instanceof ApiError && Array.isArray(e.details)) {
        const next: Record<string, string> = {};
        for (const d of e.details as Array<{ path: string; message: string }>)
          next[d.path] = d.message;
        setErrors(next);
      }
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next = contactProblems(
      {
        kind: form.kind,
        displayName: form.displayName,
        email: form.email,
        country: form.billing.country,
        gstin: form.gstin,
        trn: form.trn,
        vatRegistered: form.vatRegistered,
        iban: form.iban,
      },
      isIndia(),
    );
    setErrors(next);
    if (!Object.keys(next).length) save.mutate();
  }

  const banner = save.error ? save.error.message : null;
  const err = (k: string) => errors[k];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[680px]">
        <form noValidate onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>
              {contact
                ? t(`salesPages.contactDrawer.title.${form.kind}`)
                : t(`salesPages.contactList.newContact.${form.kind}`)}
            </SheetTitle>
            <SheetDescription>
              {contact
                ? nameFor(lang, contact.displayName, contact.displayNameAr)
                : t(`salesPages.contactDrawer.description.${form.kind}`)}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4">
            <ErrorBanner>{banner}</ErrorBanner>
            {chooseKind ? (
              <Field label={t('salesPages.contactDetail.type')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.kind}
                    onChange={(e) => set('kind', e.target.value as Form['kind'])}
                  >
                    <option value="customer">{t('salesPages.contactKind.customer.one')}</option>
                    <option value="vendor">{t('salesPages.contactKind.vendor.one')}</option>
                  </NativeSelect>
                )}
              </Field>
            ) : null}

            <Group title={t('salesPages.contactDrawer.nameAndContact')}>
              <Field
                label={t(`salesPages.contactDrawer.displayName.${form.kind}`)}
                className="col-span-2"
                error={err('displayName')}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.displayName}
                    maxLength={200}
                    autoFocus
                    aria-invalid={Boolean(err('displayName'))}
                    onChange={(e) => set('displayName', e.target.value)}
                  />
                )}
              </Field>
              {arabicOn ? (
                <ArabicField
                  label={t('salesPages.contactDrawer.arabicDisplayName')}
                  maxLength={200}
                  value={form.displayNameAr}
                  error={err('displayNameAr')}
                  onChange={(v) => set('displayNameAr', v)}
                />
              ) : null}
              <Field label={t('salesPages.contactDetail.field.companyName')}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.companyName}
                    maxLength={200}
                    onChange={(e) => set('companyName', e.target.value)}
                  />
                )}
              </Field>
              {arabicOn ? (
                <ArabicField
                  label={t('settingsPages.company.languages.arabicCompanyName')}
                  maxLength={200}
                  value={form.companyNameAr}
                  error={err('companyNameAr')}
                  onChange={(v) => set('companyNameAr', v)}
                />
              ) : null}
              <Field label={t('salesPages.contactDetail.field.contactName')}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.contactName}
                    maxLength={200}
                    onChange={(e) => set('contactName', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('salesPages.contactDetail.field.email')} error={err('email')}>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    value={form.email}
                    maxLength={200}
                    aria-invalid={Boolean(err('email'))}
                    onChange={(e) => set('email', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('salesPages.contactDrawer.phone')}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.phone}
                    maxLength={40}
                    onChange={(e) => set('phone', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('salesPages.contactDrawer.mobile')}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.mobile}
                    maxLength={40}
                    onChange={(e) => set('mobile', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('salesPages.contactDetail.field.website')}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.website}
                    maxLength={200}
                    onChange={(e) => set('website', e.target.value)}
                  />
                )}
              </Field>
            </Group>

            <Group
              title={
                form.kind === 'vendor'
                  ? t('salesPages.contactDetail.field.address')
                  : t('salesPages.contactDrawer.addresses')
              }
            >
              <AddressFields
                which="billing"
                value={form.billing}
                onChange={(k, v) => setAddr('billing', k, v)}
                emirateError={err('emirate')}
              />
              {form.kind === 'customer' ? (
                <>
                  <Checkbox
                    className="col-span-2"
                    label={t('salesPages.contactDrawer.shippingSameAsBilling')}
                    checked={form.sameAsBilling}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        sameAsBilling: e.target.checked,
                        shipping: e.target.checked ? f.billing : f.shipping,
                      }))
                    }
                  />
                  {!form.sameAsBilling ? (
                    <AddressFields
                      which="shipping"
                      value={form.shipping}
                      onChange={(k, v) => setAddr('shipping', k, v)}
                    />
                  ) : null}
                </>
              ) : null}
            </Group>

            <Group title={isIndia() ? 'GST' : 'VAT'}>
              {form.billing.country === 'IN' ? (
                <>
                  <Field
                    label="GSTIN"
                    hint={t('salesPages.contactDrawer.gstinHint')}
                    error={err('gstin')}
                    className="col-span-2"
                  >
                    {(id) => (
                      <Input
                        id={id}
                        value={form.gstin}
                        placeholder="29AAGCB7383J1Z4"
                        aria-invalid={Boolean(err('gstin'))}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          set('gstin', v);
                          const code = v.replace(/\s+/g, '').slice(0, 2);
                          if (GST_STATES.some(([c]) => c === code))
                            setAddr('billing', 'state', code);
                        }}
                      />
                    )}
                  </Field>
                </>
              ) : form.billing.country === 'AE' ? (
                <>
                  <Checkbox
                    className="col-span-2"
                    label={t(`salesPages.contactDrawer.vatRegistered.${form.kind}`)}
                    checked={form.vatRegistered}
                    onChange={(e) => set('vatRegistered', e.target.checked)}
                  />
                  <Field
                    label="TRN"
                    hint={t('salesPages.contactDrawer.trnHint')}
                    error={err('trn')}
                  >
                    {(id) => (
                      <Input
                        id={id}
                        value={form.trn}
                        inputMode="numeric"
                        placeholder="100 1234567 00 003"
                        aria-invalid={Boolean(err('trn'))}
                        onChange={(e) => set('trn', e.target.value)}
                      />
                    )}
                  </Field>
                </>
              ) : (
                <p className="col-span-2 text-xs text-muted-foreground">
                  {isIndia()
                    ? t('salesPages.contactDrawer.outsideIndia')
                    : t('salesPages.contactDrawer.outsideUae')}
                </p>
              )}
              <Field
                label={
                  isIndia()
                    ? t('salesPages.contactDetail.field.defaultGstRate')
                    : t('salesPages.contactDetail.field.defaultVatTreatment')
                }
                hint={t(`salesPages.contactDrawer.defaultTaxHint.${form.kind}`)}
                error={err('defaultTaxCodeId')}
              >
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.defaultTaxCodeId}
                    onChange={(e) => set('defaultTaxCodeId', e.target.value)}
                  >
                    <option value="">{t('salesPages.contactDrawer.fromProductOrAccount')}</option>
                    {codes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.code} · {nameFor(lang, c.name, c.nameAr)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            </Group>

            <Group title={t('salesPages.contactDetail.filter.payment')}>
              {arabicOn ? (
                <Field
                  label={t('salesPages.contactDrawer.documentLanguage')}
                  hint={t('salesPages.contactDrawer.documentLanguageHint')}
                >
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.documentLanguage}
                      onChange={(e) =>
                        set('documentLanguage', e.target.value as Form['documentLanguage'])
                      }
                    >
                      <option value="">{t('salesPages.contactDrawer.companyDefault')}</option>
                      <option value="both">{t('language.both')}</option>
                      <option value="ar">{t('salesPages.sendDialog.arabic')}</option>
                      <option value="en">{t('salesPages.sendDialog.english')}</option>
                    </NativeSelect>
                  )}
                </Field>
              ) : null}
              <Field label={t('salesPages.contactDetail.field.terms')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.paymentTermsDays}
                    onChange={(e) => set('paymentTermsDays', e.target.value)}
                  >
                    {TERMS.map((term) => (
                      <option key={term.days} value={term.days}>
                        {term.label}
                      </option>
                    ))}
                    {!TERMS.some((term) => String(term.days) === form.paymentTermsDays) ? (
                      <option value={form.paymentTermsDays}>
                        {termsLabel(Number(form.paymentTermsDays))}
                      </option>
                    ) : null}
                  </NativeSelect>
                )}
              </Field>
              <Field
                label={t('salesPages.contactDetail.field.currency')}
                error={err('currency')}
                hint={
                  contact?.hasTransactions
                    ? t(`salesPages.contactDrawer.currencyFixed.${form.kind}`)
                    : currencies.data && !currencies.data.enabled
                      ? t('salesPages.contactDrawer.multicurrencyOff')
                      : undefined
                }
              >
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.currency || home}
                    disabled={
                      Boolean(contact?.hasTransactions) || !(currencies.data?.enabled ?? false)
                    }
                    onChange={(e) => set('currency', e.target.value)}
                  >
                    <option value={home}>
                      {t('salesPages.contactDrawer.homeCurrency', {
                        currency: homeCurrencyLabel(home),
                      })}
                    </option>
                    {(currencies.data?.currencies ?? []).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} – {c.name}
                      </option>
                    ))}
                    {form.currency &&
                    form.currency !== home &&
                    !currencies.data?.currencies.some((c) => c.code === form.currency) ? (
                      <option value={form.currency}>{form.currency}</option>
                    ) : null}
                  </NativeSelect>
                )}
              </Field>
              {form.kind === 'vendor' ? (
                <>
                  <Field
                    label={t('salesPages.contactDetail.field.defaultCategory')}
                    className="col-span-2"
                    hint={t('salesPages.contactDrawer.defaultCategoryHint')}
                    error={err('defaultAccountId')}
                  >
                    {(id) => (
                      <NativeSelect
                        id={id}
                        value={form.defaultAccountId}
                        onChange={(e) => set('defaultAccountId', e.target.value)}
                      >
                        <option value="">{t('common.none')}</option>
                        {expenseAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} · {nameFor(lang, a.fullName, a.fullNameAr)}
                          </option>
                        ))}
                      </NativeSelect>
                    )}
                  </Field>
                  <Field label={t('salesPages.contactDetail.field.bankName')}>
                    {(id) => (
                      <Input
                        id={id}
                        value={form.bankName}
                        maxLength={100}
                        onChange={(e) => set('bankName', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    label={
                      isIndia()
                        ? t('salesPages.contactDetail.field.accountIfsc')
                        : t('salesPages.contactDetail.field.iban')
                    }
                    error={err('iban')}
                  >
                    {(id) => (
                      <Input
                        id={id}
                        value={form.iban}
                        placeholder={
                          isIndia() ? '50200012345678 HDFC0000123' : 'AE07 0331 2345 6789 0123 456'
                        }
                        aria-invalid={Boolean(err('iban'))}
                        onChange={(e) => set('iban', e.target.value)}
                      />
                    )}
                  </Field>
                </>
              ) : null}
              <p className="col-span-2 text-xs text-muted-foreground">
                {t('salesPages.contactDrawer.openingNote')}
              </p>
            </Group>

            <Group title={t('salesPages.contactDetail.field.notes')}>
              <Field label={t('salesPages.contactDetail.field.notes')} className="col-span-2">
                {(id) => (
                  <Textarea
                    id={id}
                    value={form.notes}
                    maxLength={2000}
                    onChange={(e) => set('notes', e.target.value)}
                  />
                )}
              </Field>
            </Group>
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-2 text-sm font-semibold">{title}</legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

function AddressFields({
  which,
  value,
  onChange,
  emirateError,
}: {
  which: 'billing' | 'shipping';
  value: AddressForm;
  onChange: (k: keyof AddressForm, v: string) => void;
  emirateError?: string | undefined;
}) {
  const t = useT();
  const label = (
    field: 'street' | 'line2' | 'city' | 'pinCode' | 'poBox' | 'country' | 'state' | 'emirate',
  ) => t(`salesPages.contactDrawer.address.${field}.${which}`);
  return (
    <>
      <Field label={label('street')} className="col-span-2">
        {(id) => (
          <Input
            id={id}
            value={value.line1}
            maxLength={200}
            onChange={(e) => onChange('line1', e.target.value)}
          />
        )}
      </Field>
      <Field label={label('line2')} className="col-span-2">
        {(id) => (
          <Input
            id={id}
            value={value.line2}
            maxLength={200}
            onChange={(e) => onChange('line2', e.target.value)}
          />
        )}
      </Field>
      <Field label={label('city')}>
        {(id) => (
          <Input
            id={id}
            value={value.city}
            maxLength={100}
            onChange={(e) => onChange('city', e.target.value)}
          />
        )}
      </Field>
      {value.country === 'IN' ? (
        <Field label={label('pinCode')}>
          {(id) => (
            <Input
              id={id}
              value={value.postalCode}
              maxLength={12}
              inputMode="numeric"
              onChange={(e) => onChange('postalCode', e.target.value)}
            />
          )}
        </Field>
      ) : (
        <Field label={label('poBox')}>
          {(id) => (
            <Input
              id={id}
              value={value.poBox}
              maxLength={20}
              onChange={(e) => onChange('poBox', e.target.value)}
            />
          )}
        </Field>
      )}
      <Field label={label('country')}>
        {(id) => (
          <NativeSelect
            id={id}
            value={value.country}
            onChange={(e) => onChange('country', e.target.value)}
          >
            {Object.keys(COUNTRIES).map((code) => (
              <option key={code} value={code}>
                {countryName(code)}
              </option>
            ))}
            {!COUNTRIES[value.country] ? (
              <option value={value.country}>{value.country}</option>
            ) : null}
          </NativeSelect>
        )}
      </Field>
      {value.country === 'IN' && which === 'billing' ? (
        <Field label={label('state')} hint={t('settingsPages.company.companyTab.emirateHint')}>
          {(id) => (
            <NativeSelect
              id={id}
              value={value.state}
              onChange={(e) => onChange('state', e.target.value)}
            >
              <option value="">{t('settingsPages.company.companyTab.notSet')}</option>
              {GST_STATES.map(([code, name]) => (
                <option key={code} value={code}>
                  {code} · {name}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      ) : value.country === 'AE' ? (
        <Field
          label={label('emirate')}
          hint={which === 'billing' ? t('settingsPages.company.companyTab.emirateHint') : undefined}
          error={emirateError}
        >
          {(id) => (
            <NativeSelect
              id={id}
              value={value.emirate}
              onChange={(e) => onChange('emirate', e.target.value)}
            >
              <option value="">{t('settingsPages.company.companyTab.notSet')}</option>
              {EMIRATES.map((em) => (
                <option key={em} value={em}>
                  {emirateLabel(em)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      ) : (
        <div />
      )}
    </>
  );
}
